/**
 * 📁 Archivos — vista jerárquica Finder-style:
 *   Nivel 0 (sección Archivos): carpetas por sección (Inmuebles, Autosell, Personal...)
 *   Nivel 1: dentro de Inmuebles → tarjeta por cada inmueble + "Sin entidad"
 *   Nivel 2: archivos dentro de un inmueble específico
 *
 * Permite renombrar archivos, vincularlos a entidad, mover entre carpetas, descargar, borrar.
 */
import { h, apiClient, pill, btn, emptyState, openModal, openConfirm, toast, sectionHeader, statTile, formatDate, openQuickTaskModal } from './_common.js';

// ============ DRAG & DROP STATE ============
let _dragging = null; // { kind: 'file'|'folder', id, name }

// Hacer un elemento DRAGGABLE
function makeDraggable(el, kind, id, name) {
  el.setAttribute('draggable', 'true');
  el.addEventListener('dragstart', (e) => {
    _dragging = { kind, id, name };
    el.classList.add('opacity-50');
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('application/json', JSON.stringify(_dragging)); } catch {}
  });
  el.addEventListener('dragend', () => {
    el.classList.remove('opacity-50');
    _dragging = null;
  });
}

// Hacer un elemento DROP TARGET
function makeDropTarget(el, onDrop) {
  el.addEventListener('dragover', (e) => {
    if (!_dragging) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    el.classList.add('ring-2', 'ring-gold-400/60', 'bg-gold-400/5');
  });
  el.addEventListener('dragleave', () => {
    el.classList.remove('ring-2', 'ring-gold-400/60', 'bg-gold-400/5');
  });
  el.addEventListener('drop', async (e) => {
    e.preventDefault();
    el.classList.remove('ring-2', 'ring-gold-400/60', 'bg-gold-400/5');
    if (!_dragging) return;
    try {
      await onDrop(_dragging);
    } catch (err) {
      toast('Error al mover: ' + (err.message || ''), 'error');
    } finally {
      _dragging = null;
    }
  });
}

const SECCIONES = [
  { id: 'inmuebles',  label: 'Inmuebles',  emoji: '🏠', color: 'blue',   entity: 'property',  endpoint: '/api/v1/properties', nameField: 'name' },
  { id: 'autosell',   label: 'Autosell',   emoji: '🚗', color: 'red',    entity: 'vehicle',   endpoint: '/api/v1/vehicles',   nameField: 'brand+model' },
  { id: 'personal',   label: 'Personal',   emoji: '🌟', color: 'purple', entity: null,        endpoint: null,                 nameField: null },
  { id: 'carlos-ai',  label: 'CARLOS.AI',  emoji: '🤖', color: 'green',  entity: 'project',   endpoint: '/api/v1/projects',   nameField: 'name' },
  { id: 'coco-doree', label: 'COCO/DORÉE', emoji: '🛍️', color: 'gold',   entity: 'project',   endpoint: '/api/v1/projects',   nameField: 'name' },
  { id: 'suiza-go',   label: 'Suiza-go',   emoji: '🇨🇭', color: 'gold',   entity: 'project',   endpoint: '/api/v1/projects',   nameField: 'name' },
  { id: 'salud',      label: 'Salud',      emoji: '💪', color: 'red',    entity: null,        endpoint: null,                 nameField: null },
];

// Estado de navegación: nivel actual
// special view: 'all' = todos los archivos sin importar sección
// folderId = subcarpeta custom activa
let _nav = { level: 0, seccion: null, entityType: null, entityId: null, entityName: null, view: null, folderId: null };
let _foldersCache = null;

export async function renderArchivos(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' });
  wrap.appendChild(sectionHeader('📁', 'Archivos', breadcrumb(), [
    btn('✅ + Tarea', () => openQuickTaskModal('personal'), { variant: 'secondary' }),
    btn('⬆️ Subir archivo', () => openUploadModal(() => renderArchivos(root)), {}),
    btn('⬇️ Descargar TODO', () => downloadAllFiles(), { variant: 'secondary' }),
  ]));

  // Breadcrumb navigation
  if (_nav.level > 0 || _nav.view) {
    wrap.appendChild(h('button', {
      class: 'text-sm text-gold-400 hover:text-gold-300 mb-4 flex items-center gap-1',
      onclick: () => {
        if (_nav.level === 2) _nav = { level: 1, seccion: _nav.seccion, entityType: null, entityId: null, entityName: null, view: null };
        else _nav = { level: 0, seccion: null, entityType: null, entityId: null, entityName: null, view: null };
        renderArchivos(root);
      },
    }, '← Atrás'));
  }

  // Stats + folders cache
  const [allFiles, foldersRes] = await Promise.all([
    apiClient.get('/api/v1/files').catch(() => ({ data: [] })),
    apiClient.get('/api/v1/folders').catch(() => ({ data: [] })),
  ]);
  const all = allFiles.data || [];
  _foldersCache = foldersRes.data || [];
  const statsRow = h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-5' });
  statsRow.appendChild(statTile({ label: 'Total archivos', value: all.length, emoji: '📁', color: 'gold' }));
  statsRow.appendChild(statTile({ label: 'Tamaño total', value: formatBytes(all.reduce((s, f) => s + (f.sizeBytes || 0), 0)), emoji: '💾', color: 'blue' }));
  statsRow.appendChild(statTile({ label: 'Sin vincular', value: all.filter((f) => !f.linkedType).length, emoji: '🔓', color: 'purple' }));
  statsRow.appendChild(statTile({ label: 'PDFs', value: all.filter((f) => (f.mimeType || '').includes('pdf')).length, emoji: '📄', color: 'red' }));
  wrap.appendChild(statsRow);

  root.appendChild(wrap);

  // Vista según nivel
  if (_nav.view === 'all') await renderAllFilesView(wrap, all, root);
  else if (_nav.view === 'unsorted') await renderUnsortedView(wrap, all, root);
  else if (_nav.level === 0) await renderLevel0(wrap, all, root);
  else if (_nav.level === 1) await renderLevel1(wrap, all, root);
  else await renderLevel2(wrap, all, root);
}

function breadcrumb() {
  if (_nav.view === 'all') return 'TODOS tus archivos — usa 📁 para moverlos a cualquier carpeta';
  if (_nav.view === 'unsorted') return 'Archivos sin clasificar — muévelos a la carpeta correcta con 📁';
  if (_nav.level === 0) return 'Tus archivos organizados por sección y entidad';
  if (_nav.level === 1) {
    const s = SECCIONES.find((x) => x.id === _nav.seccion);
    return `${s?.emoji || '📁'} ${s?.label || ''} → elige entidad`;
  }
  return `${_nav.entityName || 'Entidad'} — archivos`;
}

// ============ NIVEL 0: carpetas por sección ============
async function renderLevel0(wrap, all, root) {
  // Drop zone global
  wrap.appendChild(h('div', {
    id: 'global-dropzone',
    class: 'mb-5 border-2 border-dashed border-base-600 hover:border-gold-400/60 rounded-xl p-6 text-center text-ink-300 transition cursor-pointer',
    onclick: () => openUploadModal(() => renderArchivos(root)),
  }, [
    h('div', { class: 'text-3xl mb-2' }, '⬆️'),
    h('p', { class: 'font-medium' }, 'Arrastra archivos aquí o haz click para subir'),
    h('p', { class: 'text-xs text-ink-400 mt-1' }, 'Le pones nombre + lo vinculas a la entidad correcta'),
  ]));
  setupDropZone(wrap.querySelector('#global-dropzone'), () => renderArchivos(root));

  // Grid de secciones — primero 2 cards especiales (Todos + Sin clasificar)
  const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' });

  // CARD especial: TODOS LOS ARCHIVOS
  grid.appendChild(h('div', {
    class: 'bg-gradient-to-br from-gold-500/15 via-base-800/40 to-base-800/40 border-2 border-gold-400/40 hover:border-gold-400 rounded-xl p-5 cursor-pointer transition group',
    onclick: () => { _nav = { level: 0, seccion: null, entityType: null, entityId: null, entityName: null, view: 'all' }; renderArchivos(root); },
  }, [
    h('div', { class: 'flex items-center justify-between mb-3' }, [
      h('div', { class: 'flex items-center gap-3' }, [
        h('div', { class: 'w-14 h-14 rounded-2xl bg-gold-500/20 border border-gold-400/50 flex items-center justify-center text-3xl' }, '📦'),
        h('div', {}, [
          h('h3', { class: 'font-display font-semibold text-lg text-gold-300' }, 'Todos los archivos'),
          h('p', { class: 'text-xs text-ink-400' }, `${all.length} archivo${all.length !== 1 ? 's' : ''} · ver y mover desde aquí`),
        ]),
      ]),
      h('span', { class: 'text-xl text-gold-400 group-hover:text-gold-300 transition' }, '→'),
    ]),
    h('div', { class: 'h-1 rounded-full bg-gold-500/30 overflow-hidden' }, [
      h('div', { class: 'h-full bg-gold-400', style: `width: 100%` }),
    ]),
  ]));

  // CARD especial: SIN CLASIFICAR (archivos sin sección)
  const unsorted = all.filter((f) => !f.department);
  grid.appendChild(h('div', {
    class: `bg-base-800/40 border-2 ${unsorted.length > 0 ? 'border-purple-400/40 hover:border-purple-400' : 'border-base-700 hover:border-purple-400/40'} border-dashed rounded-xl p-5 cursor-pointer transition group`,
    onclick: () => { _nav = { level: 0, seccion: null, entityType: null, entityId: null, entityName: null, view: 'unsorted' }; renderArchivos(root); },
  }, [
    h('div', { class: 'flex items-center justify-between mb-3' }, [
      h('div', { class: 'flex items-center gap-3' }, [
        h('div', { class: 'w-14 h-14 rounded-2xl bg-purple-500/15 border border-purple-400/30 flex items-center justify-center text-3xl' }, '🗂️'),
        h('div', {}, [
          h('h3', { class: 'font-display font-semibold text-lg' }, 'Sin clasificar'),
          h('p', { class: 'text-xs text-ink-400' }, unsorted.length === 0 ? 'Nada por clasificar' : `${unsorted.length} archivo(s) · muévelos a su carpeta`),
        ]),
      ]),
      h('span', { class: 'text-xl text-ink-400 group-hover:text-purple-300 transition' }, '→'),
    ]),
    h('div', { class: 'h-1 rounded-full bg-purple-500/30 overflow-hidden' }, [
      h('div', { class: 'h-full bg-purple-400', style: `width: ${Math.min(100, unsorted.length * 10)}%` }),
    ]),
  ]));

  for (const s of SECCIONES) {
    const count = all.filter((f) => f.department === s.id).length;
    const sectionCard = h('div', {
      class: `bg-base-800/40 border border-base-700 hover:border-${s.color}-400/40 rounded-xl p-5 cursor-pointer transition group`,
      onclick: () => { _nav = { level: 1, seccion: s.id, entityType: null, entityId: null, entityName: null, view: null, folderId: null }; renderArchivos(root); },
    }, [
      h('div', { class: 'flex items-center justify-between mb-3' }, [
        h('div', { class: 'flex items-center gap-3' }, [
          h('div', { class: `w-14 h-14 rounded-2xl bg-${s.color}-500/15 border border-${s.color}-400/30 flex items-center justify-center text-3xl` }, s.emoji),
          h('div', {}, [
            h('h3', { class: 'font-display font-semibold text-lg' }, s.label),
            h('p', { class: 'text-xs text-ink-400' }, count === 0 ? 'Sin archivos' : `${count} archivo${count > 1 ? 's' : ''}`),
          ]),
        ]),
        h('span', { class: 'text-xl text-ink-400 group-hover:text-gold-300 transition' }, '🎯'),
      ]),
      h('div', { class: `h-1 rounded-full bg-${s.color}-500/30 overflow-hidden` }, [
        h('div', { class: `h-full bg-${s.color}-400`, style: `width: ${Math.min(100, count * 5)}%` }),
      ]),
    ]);
    // ✅ DROP TARGET: arrastrar archivo aquí → mover a esta sección
    makeDropTarget(sectionCard, async (item) => {
      if (item.kind === 'file') {
        await apiClient.patch(`/api/v1/files/${item.id}`, { department: s.id, linkedType: null, linkedId: null, folderId: null });
        toast(`Movido a ${s.label}`, 'success');
        renderArchivos(root);
      } else if (item.kind === 'folder') {
        await apiClient.patch(`/api/v1/folders/${item.id}`, { department: s.id, parentId: null });
        toast(`Carpeta movida a ${s.label}`, 'success');
        renderArchivos(root);
      }
    });
    grid.appendChild(sectionCard);
  }
  wrap.appendChild(grid);
}

// ============ VISTA: TODOS LOS ARCHIVOS ============
async function renderAllFilesView(wrap, all, root) {
  // Búsqueda + filtro de tipo
  const filterState = { q: '', dept: '', type: '' };

  wrap.appendChild(h('div', { class: 'flex items-center gap-3 mb-4' }, [
    h('span', { class: 'text-4xl' }, '📦'),
    h('div', { class: 'flex-1' }, [
      h('h3', { class: 'font-display font-bold text-xl' }, 'Todos los archivos'),
      h('p', { class: 'text-sm text-ink-400' }, `${all.length} archivo(s) en total · Click 📁 en cada archivo para moverlo a una carpeta`),
    ]),
  ]));

  // Controles de filtro
  wrap.appendChild(h('div', { class: 'flex flex-wrap gap-2 mb-4' }, [
    h('input', { type: 'search', placeholder: '🔍 Buscar...', class: 'flex-1 min-w-[180px] bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm',
      oninput: (e) => { filterState.q = e.target.value.toLowerCase(); refresh(); } }),
    h('select', { class: 'bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm',
      onchange: (e) => { filterState.dept = e.target.value; refresh(); } }, [
      h('option', { value: '' }, 'Todas las secciones'),
      h('option', { value: '__none__' }, '🗂️ Sin sección'),
      ...SECCIONES.map((s) => {
        const opt = document.createElement('option');
        opt.value = s.id; opt.textContent = `${s.emoji} ${s.label}`;
        return opt;
      }),
    ]),
    h('select', { class: 'bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm',
      onchange: (e) => { filterState.type = e.target.value; refresh(); } }, [
      h('option', { value: '' }, 'Todos los tipos'),
      h('option', { value: 'pdf' }, '📄 PDF'),
      h('option', { value: 'image' }, '🖼️ Imágenes'),
      h('option', { value: 'sheet' }, '📊 Hojas cálculo'),
      h('option', { value: 'doc' }, '📝 Documentos'),
      h('option', { value: 'video' }, '🎬 Vídeo'),
    ]),
  ]));

  const listBox = h('div', { id: 'all-files-list' });
  wrap.appendChild(listBox);

  async function refresh() {
    let filtered = [...all];
    if (filterState.q) {
      filtered = filtered.filter((f) => [f.name, f.originalName, f.description].some((x) => (x || '').toLowerCase().includes(filterState.q)));
    }
    if (filterState.dept === '__none__') filtered = filtered.filter((f) => !f.department);
    else if (filterState.dept) filtered = filtered.filter((f) => f.department === filterState.dept);
    if (filterState.type) {
      filtered = filtered.filter((f) => {
        const mime = (f.mimeType || '').toLowerCase();
        const ext = (f.originalName || f.name || '').split('.').pop()?.toLowerCase() || '';
        if (filterState.type === 'pdf') return mime.includes('pdf') || ext === 'pdf';
        if (filterState.type === 'image') return mime.startsWith('image') || ['png','jpg','jpeg','gif','webp'].includes(ext);
        if (filterState.type === 'sheet') return ['xlsx','xls','csv'].includes(ext) || mime.includes('sheet');
        if (filterState.type === 'doc') return ['docx','doc','md','txt'].includes(ext) || mime.includes('word');
        if (filterState.type === 'video') return mime.startsWith('video') || ['mp4','mov','avi','mkv'].includes(ext);
        return true;
      });
    }
    listBox.innerHTML = '';
    if (filtered.length === 0) {
      listBox.appendChild(emptyState('📦', 'Sin archivos coincidentes', 'Cambia los filtros o sube algo.'));
      return;
    }
    // Grupos por sección
    const bySection = {};
    filtered.forEach((f) => {
      const key = f.department || '__none__';
      (bySection[key] = bySection[key] || []).push(f);
    });
    // Pintar secciones que tienen archivos
    const orderedKeys = ['__none__', ...SECCIONES.map((s) => s.id)];
    for (const key of orderedKeys) {
      if (!bySection[key] || bySection[key].length === 0) continue;
      const sec = key === '__none__' ? null : SECCIONES.find((s) => s.id === key);
      const label = sec ? `${sec.emoji} ${sec.label}` : '🗂️ Sin sección';
      listBox.appendChild(h('div', { class: 'mb-5' }, [
        h('div', { class: 'flex items-center gap-2 mb-2 pb-1 border-b border-base-700/50' }, [
          h('h4', { class: 'font-semibold text-sm uppercase tracking-wider text-ink-300' }, label),
          h('span', { class: 'text-xs text-ink-400' }, `· ${bySection[key].length}`),
        ]),
        h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' },
          bySection[key].map((f) => fileCard(f, root, []))),
      ]));
    }
  }
  await refresh();
}

// ============ VISTA: SIN CLASIFICAR ============
async function renderUnsortedView(wrap, all, root) {
  const unsorted = all.filter((f) => !f.department);
  wrap.appendChild(h('div', { class: 'flex items-center gap-3 mb-4' }, [
    h('span', { class: 'text-4xl' }, '🗂️'),
    h('div', { class: 'flex-1' }, [
      h('h3', { class: 'font-display font-bold text-xl' }, 'Sin clasificar'),
      h('p', { class: 'text-sm text-ink-400' }, `${unsorted.length} archivo(s) · Click 📁 para enviarlos a su carpeta`),
    ]),
  ]));
  if (unsorted.length === 0) {
    wrap.appendChild(emptyState('✨', '¡Todo organizado!', 'No tienes archivos sin clasificar.'));
    return;
  }
  // Help banner
  wrap.appendChild(h('div', { class: 'bg-purple-500/10 border border-purple-400/30 rounded-lg p-3 mb-4 text-sm text-purple-200' }, [
    h('strong', {}, '💡 Tip: '),
    'En cada archivo, pulsa ',
    h('span', { class: 'text-emerald-300 font-mono bg-base-900/50 px-1.5 py-0.5 rounded' }, '📁'),
    ' para moverlo a Inmuebles, Autosell o cualquier otra carpeta.',
  ]));
  const filesGrid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' });
  unsorted.forEach((f) => filesGrid.appendChild(fileCard(f, root, [])));
  wrap.appendChild(filesGrid);
}

// ============ NIVEL 1: dentro de una sección, mostrar entidades ============
async function renderLevel1(wrap, all, root) {
  const s = SECCIONES.find((x) => x.id === _nav.seccion);
  if (!s) { _nav = { level: 0 }; renderArchivos(root); return; }

  // Archivos de esta sección
  const sectionFiles = all.filter((f) => f.department === s.id);

  // Entidades (si la sección tiene tipo entidad: inmuebles/vehículos/proyectos)
  let entities = [];
  if (s.endpoint) {
    const res = await apiClient.get(s.endpoint).catch(() => ({ data: [] }));
    entities = (res.data || []).map((e) => ({
      id: e.id,
      type: s.entity,
      name: s.nameField === 'brand+model' ? `${e.brand || ''} ${e.model || ''}`.trim() : (e[s.nameField] || '(sin nombre)'),
      meta: e.city || e.status || e.department || '',
    }));
  }

  // Header sección
  wrap.appendChild(h('div', { class: 'flex items-center gap-3 mb-4' }, [
    h('span', { class: 'text-4xl' }, s.emoji),
    h('div', {}, [
      h('h3', { class: 'font-display font-bold text-2xl' }, s.label),
      h('p', { class: 'text-sm text-ink-400' }, `${sectionFiles.length} archivo(s) · ${entities.length} entidad(es)`),
    ]),
  ]));

  // Si no tiene entidades (personal/salud): mostrar archivos directamente
  if (!s.endpoint) {
    if (sectionFiles.length === 0) {
      wrap.appendChild(emptyState('📁', `Sin archivos en ${s.label}`, 'Sube tu primer archivo a esta sección.', btn('⬆️ Subir archivo', () => openUploadModal(() => renderArchivos(root)))));
      return;
    }
    const filesGrid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' });
    sectionFiles.forEach((f) => filesGrid.appendChild(fileCard(f, root, entities)));
    wrap.appendChild(filesGrid);
    return;
  }

  // Grid de entidades + carpeta "Sin vincular"
  const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' });
  entities.forEach((e) => {
    const count = sectionFiles.filter((f) => f.linkedType === e.type && f.linkedId === e.id).length;
    const entityCard = h('div', {
      class: `bg-base-800/40 border border-base-700 hover:border-${s.color}-400/40 rounded-xl p-4 cursor-pointer transition group`,
      onclick: () => { _nav = { level: 2, seccion: s.id, entityType: e.type, entityId: e.id, entityName: e.name, view: null, folderId: null }; renderArchivos(root); },
    }, [
      h('div', { class: 'flex items-start justify-between mb-2' }, [
        h('div', { class: 'flex items-start gap-2' }, [
          h('span', { class: 'text-2xl' }, '📂'),
          h('div', { class: 'min-w-0' }, [
            h('h4', { class: 'font-semibold truncate' }, e.name),
            e.meta ? h('p', { class: 'text-xs text-ink-400 truncate' }, e.meta) : null,
          ]),
        ]),
        pill(`${count}`, count > 0 ? s.color : 'gray'),
      ]),
    ]);
    // DROP: arrastrar archivo aquí → mover a esta entidad
    makeDropTarget(entityCard, async (item) => {
      if (item.kind === 'file') {
        await apiClient.patch(`/api/v1/files/${item.id}`, { department: s.id, linkedType: e.type, linkedId: e.id });
        toast(`Movido a ${e.name}`, 'success');
        renderArchivos(root);
      } else if (item.kind === 'folder') {
        await apiClient.patch(`/api/v1/folders/${item.id}`, { department: s.id, parentId: `${e.type}:${e.id}` });
        toast(`Carpeta movida a ${e.name}`, 'success');
        renderArchivos(root);
      }
    });
    grid.appendChild(entityCard);
  });
  // Sin vincular
  const unlinked = sectionFiles.filter((f) => !f.linkedId);
  const unlinkedCard = h('div', {
    class: 'bg-base-800/40 border border-base-700 border-dashed hover:border-purple-400/40 rounded-xl p-4 cursor-pointer transition',
    onclick: () => { _nav = { level: 2, seccion: s.id, entityType: null, entityId: null, entityName: 'Sin vincular', view: null, folderId: null }; renderArchivos(root); },
  }, [
    h('div', { class: 'flex items-start justify-between mb-2' }, [
      h('div', { class: 'flex items-start gap-2' }, [
        h('span', { class: 'text-2xl' }, '🗂️'),
        h('div', {}, [
          h('h4', { class: 'font-semibold' }, 'Sin vincular'),
          h('p', { class: 'text-xs text-ink-400' }, 'Archivos generales de la sección'),
        ]),
      ]),
      pill(`${unlinked.length}`, unlinked.length > 0 ? 'purple' : 'gray'),
    ]),
  ]);
  makeDropTarget(unlinkedCard, async (item) => {
    if (item.kind === 'file') {
      await apiClient.patch(`/api/v1/files/${item.id}`, { department: s.id, linkedType: null, linkedId: null });
      toast('Movido a Sin vincular', 'success');
      renderArchivos(root);
    }
  });
  grid.appendChild(unlinkedCard);
  wrap.appendChild(grid);
}

// ============ NIVEL 2: archivos dentro de entidad ============
async function renderLevel2(wrap, all, root) {
  const s = SECCIONES.find((x) => x.id === _nav.seccion);
  // Filtrar archivos de esta entidad (o sin entidad si entityId null) Y de la subcarpeta activa (folderId null = raíz)
  const filtered = all.filter((f) => {
    if (f.department !== _nav.seccion) return false;
    if (_nav.entityId == null) {
      if (f.linkedId) return false;
    } else {
      if (f.linkedType !== _nav.entityType || f.linkedId !== _nav.entityId) return false;
    }
    if (_nav.folderId) return f.folderId === _nav.folderId;
    return !f.folderId;
  });

  // Subcarpetas custom de esta entidad/sección
  // Scope de carpetas: las que coincidan con la entidad O sin parentId pero misma sección
  const scopeFolders = (_foldersCache || []).filter((fd) => {
    if (fd.department !== _nav.seccion) return false;
    // parentId encodes entity: usamos parentId = `${entityType}:${entityId}` o null
    const entityKey = _nav.entityId ? `${_nav.entityType}:${_nav.entityId}` : null;
    return (fd.parentId || null) === (_nav.folderId || entityKey);
  });

  // Header
  wrap.appendChild(h('div', { class: 'flex items-center gap-3 mb-4 flex-wrap' }, [
    h('span', { class: 'text-4xl' }, _nav.entityId ? '📂' : '🗂️'),
    h('div', { class: 'flex-1' }, [
      h('h3', { class: 'font-display font-bold text-xl' }, _nav.entityName),
      h('p', { class: 'text-sm text-ink-400' }, `${filtered.length} archivo(s) · ${s?.label || ''}`),
    ]),
    btn('📁 Nueva carpeta', () => openCreateFolderModal(_nav.seccion, _nav.entityId ? `${_nav.entityType}:${_nav.entityId}` : null, _nav.folderId, () => renderArchivos(root)), { variant: 'secondary' }),
    btn('⬆️ Subir aquí', () => openUploadModal(() => renderArchivos(root), {
      department: _nav.seccion,
      linkedType: _nav.entityType,
      linkedId: _nav.entityId,
      folderId: _nav.folderId,
    })),
  ]));

  // Path of folder breadcrumb (si estamos dentro de una subcarpeta) — ← Raíz es DROP TARGET
  if (_nav.folderId) {
    const folder = (_foldersCache || []).find((fd) => fd.id === _nav.folderId);
    if (folder) {
      const backBtn = h('button', {
        class: 'text-gold-400 hover:text-gold-300 border border-gold-400/30 rounded-lg px-3 py-1.5 transition',
        onclick: () => { _nav.folderId = null; renderArchivos(root); },
        title: 'Volver / suelta aquí para sacar de la subcarpeta',
      }, '← Raíz de ' + (_nav.entityName || 'sección'));
      makeDropTarget(backBtn, async (item) => {
        if (item.kind === 'file') {
          await apiClient.patch(`/api/v1/files/${item.id}`, { folderId: null });
          toast('Sacado de la subcarpeta', 'success');
          renderArchivos(root);
        } else if (item.kind === 'folder' && item.id !== _nav.folderId) {
          await apiClient.patch(`/api/v1/folders/${item.id}`, { parentId: _nav.entityId ? `${_nav.entityType}:${_nav.entityId}` : null });
          toast('Subcarpeta sacada al nivel padre', 'success');
          renderArchivos(root);
        }
      });
      wrap.appendChild(h('div', { class: 'flex items-center gap-2 mb-3 text-sm' }, [
        backBtn,
        h('span', { class: 'text-ink-400' }, '/'),
        h('span', { class: 'text-gold-300 font-semibold' }, `📁 ${folder.name}`),
      ]));
    }
  }

  // Banner ayuda drag & drop
  wrap.appendChild(h('div', { class: 'bg-sky-500/10 border border-sky-400/30 rounded-lg p-2.5 mb-3 text-xs text-sky-200 flex items-center gap-2' }, [
    h('span', { class: 'text-base' }, '💡'),
    h('span', {}, 'Tip: arrastra archivos a las subcarpetas, o a "← Raíz" para sacarlos.'),
  ]));

  // Subcarpetas (cards) — DRAGGABLES y DROP TARGETS
  if (scopeFolders.length > 0) {
    wrap.appendChild(h('h4', { class: 'text-xs uppercase tracking-wider text-ink-300 font-semibold mb-2' }, '📂 Subcarpetas · arrastra para mover'));
    const folderGrid = h('div', { class: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-4' });
    scopeFolders.forEach((fd) => {
      const fileCount = all.filter((f) => f.folderId === fd.id).length;
      const folderCard = h('div', {
        class: 'bg-base-800/40 border border-base-700 hover:border-gold-400/40 rounded-lg p-3 cursor-move transition group',
        onclick: () => { _nav.folderId = fd.id; renderArchivos(root); },
      }, [
        h('div', { class: 'flex items-start justify-between gap-2' }, [
          h('div', { class: 'flex items-center gap-2 min-w-0' }, [
            h('span', { class: 'text-2xl' }, fd.icon || '📁'),
            h('div', { class: 'min-w-0' }, [
              h('div', { class: 'font-semibold truncate text-sm', style: fd.color ? `color: ${fd.color}` : '' }, fd.name),
              h('div', { class: 'text-xs text-ink-400' }, `${fileCount} archivo(s)`),
            ]),
          ]),
          h('button', {
            class: 'text-xs text-red-400 opacity-0 group-hover:opacity-100 transition',
            onclick: async (e) => {
              e.stopPropagation();
              const ok = await openConfirm({ title: 'Eliminar carpeta', message: `"${fd.name}" — los archivos quedarán sin carpeta pero NO se borran`, danger: true, icon: '🗑️' });
              if (!ok) return;
              await apiClient.delete(`/api/v1/folders/${fd.id}`);
              toast('Carpeta eliminada', 'success');
              renderArchivos(root);
            },
          }, '🗑'),
        ]),
      ]);
      // DRAGGABLE: arrastrar esta carpeta
      makeDraggable(folderCard, 'folder', fd.id, fd.name);
      // DROP TARGET: soltar archivos/carpetas DENTRO de esta carpeta
      makeDropTarget(folderCard, async (item) => {
        if (item.kind === 'file') {
          await apiClient.patch(`/api/v1/files/${item.id}`, { folderId: fd.id, department: fd.department });
          toast(`📁 → ${fd.name}`, 'success');
          renderArchivos(root);
        } else if (item.kind === 'folder' && item.id !== fd.id) {
          await apiClient.patch(`/api/v1/folders/${item.id}`, { parentId: fd.id, department: fd.department });
          toast(`Subcarpeta movida dentro de ${fd.name}`, 'success');
          renderArchivos(root);
        }
      });
      folderGrid.appendChild(folderCard);
    });
    wrap.appendChild(folderGrid);
  }

  // Archivos
  if (filtered.length > 0) {
    wrap.appendChild(h('h4', { class: 'text-xs uppercase tracking-wider text-ink-300 font-semibold mb-2' }, '📄 Archivos'));
  }
  if (filtered.length === 0 && scopeFolders.length === 0) {
    wrap.appendChild(emptyState('📁', 'Sin archivos ni carpetas aquí', 'Sube el primero con ⬆️ Subir aquí o crea una subcarpeta con 📁 Nueva carpeta.'));
    return;
  }
  if (filtered.length > 0) {
    const filesGrid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' });
    const entities = await loadEntitiesForSection(_nav.seccion);
    filtered.forEach((f) => filesGrid.appendChild(fileCard(f, root, entities)));
    wrap.appendChild(filesGrid);
  }
}

// ============ MODAL: Crear carpeta custom ============
function openCreateFolderModal(department, parentEntityKey, parentFolderId, onDone) {
  openModal({
    title: 'Nueva carpeta',
    icon: '📁',
    fields: [
      { name: 'name', label: 'Nombre', required: true, placeholder: 'p.ej. Compra vivienda, Gastos fijos, Archivos 2026' },
      { name: 'icon', label: 'Icono (emoji)', value: '📁', placeholder: '📁' },
      { name: 'color', label: 'Color (hex)', value: '#E8A020', placeholder: '#E8A020' },
    ],
    onSubmit: async (vals) => {
      await apiClient.post('/api/v1/folders', {
        name: vals.name,
        icon: vals.icon || '📁',
        color: vals.color || '#E8A020',
        department,
        parentId: parentFolderId || parentEntityKey || null,
      });
      toast('Carpeta creada ✓', 'success');
      onDone();
    },
  });
}

async function loadEntitiesForSection(seccionId) {
  const s = SECCIONES.find((x) => x.id === seccionId);
  if (!s?.endpoint) return [];
  const res = await apiClient.get(s.endpoint).catch(() => ({ data: [] }));
  return (res.data || []).map((e) => ({
    id: e.id,
    type: s.entity,
    name: s.nameField === 'brand+model' ? `${e.brand || ''} ${e.model || ''}`.trim() : (e[s.nameField] || '(sin nombre)'),
  }));
}

// ============ FILE CARD ============
function fileCard(f, root, entities = []) {
  const ext = (f.originalName || f.name || '').split('.').pop()?.toLowerCase() || '';
  const emoji = pickEmoji(ext, f.mimeType);
  const card = h('div', {
    class: 'bg-base-800/50 border border-base-700 hover:border-gold-400/40 rounded-xl p-4 transition group flex flex-col cursor-move',
    title: 'Arrastra a una carpeta para moverlo',
  }, [
    h('div', { class: 'flex items-start gap-3 mb-2' }, [
      h('div', { class: 'text-4xl' }, emoji),
      h('div', { class: 'flex-1 min-w-0' }, [
        h('h4', { class: 'font-semibold truncate', title: f.name }, f.name),
        h('p', { class: 'text-xs text-ink-400 truncate' }, f.originalName || ''),
        h('div', { class: 'flex items-center gap-2 text-xs text-ink-300 mt-1' }, [
          h('span', {}, formatBytes(f.sizeBytes || 0)),
          h('span', {}, '·'),
          h('span', {}, formatDate(f.createdAt)),
        ]),
      ]),
    ]),
    f.description ? h('p', { class: 'text-xs text-ink-300 line-clamp-2 mb-2' }, f.description) : null,
    h('div', { class: 'flex items-center justify-between mt-auto pt-2 border-t border-base-700/50' }, [
      h('div', { class: 'flex gap-1 flex-wrap' }, [
        (((f.mimeType || '').includes('html') || (f.mimeType || '').includes('pdf') || /\.(html?|pdf)$/i.test(f.originalName || f.name || ''))
          ? h('a', {
              href: `/api/v1/files/${f.id}/preview`,
              target: '_blank',
              rel: 'noopener',
              class: 'text-xs text-emerald-400 hover:text-emerald-300 px-2 py-1 rounded hover:bg-base-700',
              title: 'Ver en navegador',
            }, '👁️')
          : null),
        h('a', {
          href: `/api/v1/files/${f.id}/download`,
          class: 'text-xs text-gold-400 hover:text-gold-300 px-2 py-1 rounded hover:bg-base-700',
          title: 'Descargar',
        }, '⬇️'),
        h('button', {
          class: 'text-xs text-sky-400 hover:text-sky-300 px-2 py-1 rounded hover:bg-base-700',
          title: 'Renombrar / Editar',
          onclick: () => openFileEditModal(f, entities, () => renderArchivos(root)),
        }, '✏️'),
        h('button', {
          class: 'text-xs text-emerald-400 hover:text-emerald-300 px-2 py-1 rounded hover:bg-base-700',
          title: 'Mover a otra carpeta',
          onclick: () => openMoveFileModal(f, () => renderArchivos(root)),
        }, '📁'),
        h('button', {
          class: 'text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-base-700',
          title: 'Eliminar',
          onclick: async () => {
            const ok = await openConfirm({ title: 'Eliminar archivo', message: f.name, danger: true, icon: '🗑️' });
            if (!ok) return;
            await apiClient.delete(`/api/v1/files/${f.id}`);
            toast('Archivo eliminado', 'success');
            renderArchivos(root);
          },
        }, '🗑️'),
      ]),
    ]),
  ]);
  // ✅ DRAGGABLE: arrastrar el archivo a una carpeta
  makeDraggable(card, 'file', f.id, f.name);
  return card;
}

function pickEmoji(ext, mime) {
  if (ext === 'pdf' || (mime || '').includes('pdf')) return '📄';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return '🖼️';
  if (['xlsx', 'xls', 'csv'].includes(ext)) return '📊';
  if (['docx', 'doc'].includes(ext)) return '📝';
  if (['mp4', 'mov', 'avi', 'mkv'].includes(ext)) return '🎬';
  if (['mp3', 'wav', 'm4a'].includes(ext)) return '🎵';
  if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
  if (['txt', 'md'].includes(ext)) return '📃';
  return '📦';
}

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  while (bytes >= 1024 && i < units.length - 1) { bytes /= 1024; i++; }
  return `${bytes.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

// ============ MODALS ============
function openUploadModal(onDone, preset = {}) {
  // Crear input file hidden y modal con nombre + vinculación
  const overlay = h('div', {
    class: 'fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4',
    onclick: (e) => { if (e.target === overlay) overlay.remove(); },
  });
  let selectedFile = null;

  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-lg w-full p-6' }, [
    h('div', { class: 'flex items-center justify-between mb-4' }, [
      h('h3', { class: 'font-display font-bold text-lg flex items-center gap-2' }, ['⬆️', ' Subir archivo']),
      h('button', { class: 'text-2xl text-ink-400', onclick: () => overlay.remove() }, '×'),
    ]),
    // File picker
    h('div', { class: 'mb-3' }, [
      h('input', {
        type: 'file', id: 'archivos-file-input', class: 'block w-full text-sm text-ink-200 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-base-700 file:text-ink-100 file:cursor-pointer hover:file:bg-base-600',
        onchange: (e) => {
          selectedFile = e.target.files?.[0];
          const nameInput = modal.querySelector('input[name=customName]');
          if (selectedFile && nameInput && !nameInput.value) {
            // Preset nombre con el nombre original (sin extensión final)
            const baseName = selectedFile.name.replace(/\.[^.]+$/, '');
            nameInput.value = baseName;
          }
          const info = modal.querySelector('#file-info');
          if (info && selectedFile) {
            info.textContent = `${selectedFile.name} · ${formatBytes(selectedFile.size)}`;
          }
        },
      }),
      h('div', { id: 'file-info', class: 'text-xs text-ink-400 mt-1' }, ''),
    ]),
    // Custom name
    h('div', { class: 'mb-3' }, [
      h('label', { class: 'block text-xs font-semibold text-ink-300 uppercase tracking-wide mb-1' }, 'Nombre del archivo'),
      h('input', { name: 'customName', type: 'text', placeholder: 'p.ej. Contrato compra Vázquez Mella',
        class: 'w-full bg-base-800 border border-base-700 focus:border-gold-400 rounded-lg px-3 py-2 text-sm' }),
    ]),
    // Section
    h('div', { class: 'mb-3' }, [
      h('label', { class: 'block text-xs font-semibold text-ink-300 uppercase tracking-wide mb-1' }, 'Sección'),
      sectionSelect(preset.department),
    ]),
    // Entity (depends on section)
    h('div', { id: 'entity-row', class: 'mb-3' }),
    // Description
    h('div', { class: 'mb-4' }, [
      h('label', { class: 'block text-xs font-semibold text-ink-300 uppercase tracking-wide mb-1' }, 'Descripción (opcional)'),
      h('textarea', { name: 'description', rows: 2, placeholder: 'Notas sobre el archivo...',
        class: 'w-full bg-base-800 border border-base-700 focus:border-gold-400 rounded-lg px-3 py-2 text-sm' }),
    ]),
    // Footer
    h('div', { class: 'flex gap-2 justify-end' }, [
      h('button', { class: 'bg-base-700 hover:bg-base-600 text-ink-100 px-4 py-2 rounded-lg text-sm', onclick: () => overlay.remove() }, 'Cancelar'),
      h('button', {
        class: 'bg-gradient-to-r from-gold-400 to-gold-500 text-base-900 font-semibold px-5 py-2 rounded-lg text-sm',
        onclick: async () => {
          if (!selectedFile) { toast('Selecciona un archivo primero', 'warn'); return; }
          const name = modal.querySelector('input[name=customName]').value.trim();
          const department = modal.querySelector('select[name=department]').value;
          const description = modal.querySelector('textarea[name=description]').value.trim();
          const entitySel = modal.querySelector('select[name=entityId]');
          let linkedType = null, linkedId = null;
          if (entitySel && entitySel.value) {
            const opt = entitySel.options[entitySel.selectedIndex];
            linkedType = opt.dataset.type || null;
            linkedId = entitySel.value;
          }
          const fd = new FormData();
          fd.append('file', selectedFile);
          if (name) fd.append('name', name);
          if (department) fd.append('department', department);
          if (description) fd.append('description', description);
          if (linkedType) fd.append('linkedType', linkedType);
          if (linkedId) fd.append('linkedId', linkedId);
          try {
            const res = await fetch('/api/v1/files/upload', { method: 'POST', credentials: 'include', body: fd });
            if (!res.ok) throw new Error('Subida falló');
            toast('Archivo subido ✓', 'success');
            overlay.remove();
            onDone();
          } catch (e) {
            toast('Error: ' + e.message, 'error');
          }
        },
      }, 'Subir'),
    ]),
  ]);

  function sectionSelect(presetDept) {
    const sel = h('select', { name: 'department', class: 'w-full bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm',
      onchange: (e) => updateEntitySelect(e.target.value) }, [
      h('option', { value: '' }, '— Sin sección —'),
      ...SECCIONES.map((s) => {
        const opt = document.createElement('option');
        opt.value = s.id; opt.textContent = `${s.emoji} ${s.label}`;
        if (presetDept === s.id) opt.selected = true;
        return opt;
      }),
    ]);
    return sel;
  }
  async function updateEntitySelect(sectionId) {
    const row = modal.querySelector('#entity-row');
    row.innerHTML = '';
    const s = SECCIONES.find((x) => x.id === sectionId);
    if (!s || !s.endpoint) return;
    const entities = await loadEntitiesForSection(sectionId);
    if (entities.length === 0) {
      row.appendChild(h('p', { class: 'text-xs text-ink-400 italic' }, `Sin ${s.label} todavía.`));
      return;
    }
    row.appendChild(h('label', { class: 'block text-xs font-semibold text-ink-300 uppercase tracking-wide mb-1' }, `Vincular a ${s.label}`));
    const sel = h('select', { name: 'entityId', class: 'w-full bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm' }, [
      h('option', { value: '' }, '— Sin vincular —'),
      ...entities.map((e) => {
        const opt = document.createElement('option');
        opt.value = e.id; opt.textContent = e.name; opt.dataset.type = e.type;
        if (preset.linkedId === e.id) opt.selected = true;
        return opt;
      }),
    ]);
    row.appendChild(sel);
  }

  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  // Trigger initial entity select if preset
  if (preset.department) setTimeout(() => updateEntitySelect(preset.department), 30);
}

// ============ MOVE FILE MODAL (cross-sección, visual) ============
function openMoveFileModal(file, onDone) {
  const overlay = h('div', {
    class: 'fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4',
    onclick: (e) => { if (e.target === overlay) overlay.remove(); },
  });
  let step = 'section'; // 'section' | 'entity'
  let chosenSection = null;

  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto' });
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  render();

  async function render() {
    modal.innerHTML = '';
    // Header
    modal.appendChild(h('div', { class: 'p-5 border-b border-base-700 flex items-center justify-between sticky top-0 bg-base-900 z-10' }, [
      h('div', {}, [
        h('h3', { class: 'font-display font-bold text-lg flex items-center gap-2' }, [
          step === 'entity' ? h('button', { class: 'text-gold-400 hover:text-gold-300', onclick: () => { step = 'section'; chosenSection = null; render(); } }, '←') : null,
          h('span', {}, ['📁 Mover "', file.name, '"']),
        ]),
        h('p', { class: 'text-xs text-ink-400 mt-1' }, step === 'section' ? 'Elige sección destino' : `Elige entidad dentro de ${chosenSection?.label || ''}`),
      ]),
      h('button', { class: 'text-2xl text-ink-400', onclick: () => overlay.remove() }, '×'),
    ]));

    const body = h('div', { class: 'p-5' });
    modal.appendChild(body);

    if (step === 'section') {
      // Mostrar todas las secciones como cards
      const grid = h('div', { class: 'grid grid-cols-2 md:grid-cols-3 gap-3' });
      // Opción "Sin sección"
      grid.appendChild(sectionPickCard({ id: '', label: 'Sin sección', emoji: '🗂️', color: 'gray' }, async () => {
        await applyMove({ department: null, linkedType: null, linkedId: null });
      }));
      SECCIONES.forEach((s) => {
        grid.appendChild(sectionPickCard(s, async () => {
          if (!s.endpoint) {
            // Sección sin entidades → ya está
            await applyMove({ department: s.id, linkedType: null, linkedId: null });
          } else {
            chosenSection = s;
            step = 'entity';
            render();
          }
        }));
      });
      body.appendChild(grid);
    } else if (step === 'entity' && chosenSection) {
      const entities = await loadEntitiesForSection(chosenSection.id);
      const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 gap-3' });
      // Sin vincular
      grid.appendChild(entityPickCard({ id: null, name: 'Sin vincular', meta: 'Archivo general de la sección', emoji: '🗂️' }, async () => {
        await applyMove({ department: chosenSection.id, linkedType: null, linkedId: null });
      }));
      if (entities.length === 0) {
        body.appendChild(h('p', { class: 'text-sm text-ink-400 text-center py-6' }, `No hay entidades en ${chosenSection.label} todavía.`));
      } else {
        entities.forEach((e) => {
          grid.appendChild(entityPickCard({ ...e, emoji: '📂' }, async () => {
            await applyMove({ department: chosenSection.id, linkedType: e.type, linkedId: e.id });
          }));
        });
      }
      body.appendChild(grid);
    }
  }

  async function applyMove(payload) {
    try {
      await apiClient.patch(`/api/v1/files/${file.id}`, payload);
      toast('Archivo movido ✓', 'success');
      overlay.remove();
      onDone();
    } catch (e) {
      toast('Error: ' + (e.message || ''), 'error');
    }
  }

  function sectionPickCard(s, onPick) {
    const colorMap = {
      gold: 'border-gold-400/30 hover:border-gold-400', blue: 'border-sky-400/30 hover:border-sky-400',
      red: 'border-red-400/30 hover:border-red-400', green: 'border-emerald-400/30 hover:border-emerald-400',
      purple: 'border-purple-400/30 hover:border-purple-400', gray: 'border-base-600 hover:border-ink-300',
    };
    const isCurrent = file.department === s.id || (!file.department && s.id === '');
    return h('button', {
      class: `bg-base-800/40 border ${colorMap[s.color] || colorMap.gold} rounded-xl p-4 transition text-left ${isCurrent ? 'ring-2 ring-gold-400/40' : ''}`,
      onclick: onPick,
    }, [
      h('div', { class: 'flex items-center gap-3' }, [
        h('span', { class: 'text-3xl' }, s.emoji),
        h('div', {}, [
          h('div', { class: 'font-display font-semibold' }, s.label),
          isCurrent ? h('div', { class: 'text-[10px] text-gold-400 uppercase tracking-wider' }, 'Actual') : null,
        ]),
      ]),
    ]);
  }

  function entityPickCard(e, onPick) {
    const isCurrent = file.linkedId === e.id || (!file.linkedId && !e.id);
    return h('button', {
      class: `bg-base-800/40 border border-base-700 hover:border-gold-400/40 rounded-xl p-3 transition text-left ${isCurrent ? 'ring-2 ring-gold-400/40' : ''}`,
      onclick: onPick,
    }, [
      h('div', { class: 'flex items-center gap-2' }, [
        h('span', { class: 'text-2xl' }, e.emoji),
        h('div', { class: 'min-w-0 flex-1' }, [
          h('div', { class: 'font-semibold truncate' }, e.name),
          e.meta ? h('div', { class: 'text-xs text-ink-400 truncate' }, e.meta) : null,
          isCurrent ? h('div', { class: 'text-[10px] text-gold-400 uppercase tracking-wider' }, 'Actual') : null,
        ]),
      ]),
    ]);
  }
}

function openFileEditModal(file, entities, onDone) {
  openModal({
    title: 'Editar archivo',
    icon: '📝',
    fields: [
      { name: 'name', label: 'Nombre', value: file.name, required: true },
      { name: 'description', label: 'Descripción', type: 'textarea', rows: 3, value: file.description },
      { name: 'department', label: 'Sección', type: 'select', value: file.department || '',
        options: [{ value: '', label: '— Sin sección —' }, ...SECCIONES.map((s) => ({ value: s.id, label: `${s.emoji} ${s.label}` }))] },
      ...(entities.length > 0 ? [{
        name: 'linkedId', label: 'Vincular a entidad', type: 'select',
        value: file.linkedId || '',
        options: [{ value: '', label: '— Sin vincular —' }, ...entities.map((e) => ({ value: e.id, label: e.name }))],
      }] : []),
    ],
    onSubmit: async (vals) => {
      const payload = {
        name: vals.name,
        description: vals.description || null,
        department: vals.department || null,
        linkedId: vals.linkedId || null,
        linkedType: vals.linkedId ? (entities.find((e) => e.id === vals.linkedId)?.type || null) : null,
      };
      await apiClient.patch(`/api/v1/files/${file.id}`, payload);
      toast('Archivo actualizado', 'success');
      onDone();
    },
  });
}

// ============ DROP ZONE ============
function setupDropZone(dz, onUpload) {
  if (!dz) return;
  dz.addEventListener('dragover', (e) => { e.preventDefault(); dz.classList.add('border-gold-400/60'); });
  dz.addEventListener('dragleave', () => dz.classList.remove('border-gold-400/60'));
  dz.addEventListener('drop', async (e) => {
    e.preventDefault();
    dz.classList.remove('border-gold-400/60');
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    // Para drop directo, subir sin abrir modal pero usar nombre original
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch('/api/v1/files/upload', { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error('Subida falló');
      toast('Archivo subido (sin vincular) ✓', 'success');
      onUpload();
    } catch (err) {
      toast('Error: ' + err.message, 'error');
    }
  });
}

// Descargar TODOS los archivos como ZIP
async function downloadAllFiles() {
  try {
    toast('📦 Generando ZIP, espera unos segundos…', 'info');
    const res = await fetch('/api/v1/files/download-all', {
      credentials: 'include',
      signal: AbortSignal.timeout(120000),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Error desconocido' }));
      toast('Error: ' + (err.message || res.statusText), 'error');
      return;
    }
    const blob = await res.blob();
    const date = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'archivos-carlosos-' + date + '.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast('✅ Descarga iniciada (' + Math.round(blob.size / 1024 / 1024) + ' MB)', 'success');
  } catch (e) {
    toast('Error: ' + (e.message || e), 'error');
  }
}
